/**
 * Deterministic fake system-status data keyed to UTC (GMT-0) calendar day,
 * so every visitor sees the same numbers on a given UTC date.
 */

const CHECK_INTERVAL_MINUTES = 5
const GOOD_AVG_MS = 248
const GOOD_MIN_MS = 180
const GOOD_MAX_MS = 420

function hashString(input) {
  let hash = 2166136261
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i)
    hash = Math.imul(hash, 16777619)
  }
  return hash >>> 0
}

function createRng(seed) {
  let state = seed >>> 0
  return () => {
    state = (Math.imul(state, 1664525) + 1013904223) >>> 0
    return state / 4294967296
  }
}

function utcDateKey(date = new Date()) {
  return date.toISOString().slice(0, 10)
}

function formatDuration(totalMinutes) {
  const minutes = Math.max(0, Math.floor(totalMinutes))
  const years = Math.floor(minutes / (365 * 24 * 60))
  let rem = minutes % (365 * 24 * 60)
  const months = Math.floor(rem / (30 * 24 * 60))
  rem %= 30 * 24 * 60
  const days = Math.floor(rem / (24 * 60))
  rem %= 24 * 60
  const hours = Math.floor(rem / 60)
  const mins = rem % 60

  const parts = []
  if (years) parts.push(`${years}y`)
  if (months) parts.push(`${months}mo`)
  if (days) parts.push(`${days}d`)
  if (!years && hours) parts.push(`${hours}h`)
  if (!years && !months && mins) parts.push(`${mins}m`)
  return parts.join(' ') || '0m'
}

function formatAgo(totalSeconds) {
  const seconds = Math.max(0, Math.floor(totalSeconds))
  const minutes = Math.floor(seconds / 60)
  const remSeconds = seconds % 60
  if (minutes <= 0) {
    return `${remSeconds}s ago`
  }
  return `${minutes}m, ${remSeconds}s ago`
}

function formatChartLabel(date) {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const month = months[date.getUTCMonth()]
  const day = date.getUTCDate()
  const hours = String(date.getUTCHours()).padStart(2, '0')
  const minutes = String(date.getUTCMinutes()).padStart(2, '0')
  return `${month} ${day}, ${hours}:${minutes}`
}

function buildSeries(rng, pointCount, endMs, stepMs) {
  const values = []
  const categories = []
  let previous = GOOD_AVG_MS + (rng() - 0.5) * 40

  for (let i = 0; i < pointCount; i += 1) {
    const t = endMs - (pointCount - 1 - i) * stepMs
    const drift = (rng() - 0.48) * 28
    const spike = rng() > 0.94 ? (rng() - 0.5) * 90 : 0
    previous = Math.min(
      GOOD_MAX_MS,
      Math.max(GOOD_MIN_MS, previous + drift + spike),
    )
    values.push(Math.round(previous * 100) / 100)
    categories.push(formatChartLabel(new Date(t)))
  }

  return { values, categories }
}

function summarize(values) {
  if (!values.length) {
    return { average: 0, minimum: 0, maximum: 0 }
  }
  const minimum = Math.min(...values)
  const maximum = Math.max(...values)
  const average = values.reduce((sum, value) => sum + value, 0) / values.length
  return {
    average: Math.round(average * 100) / 100,
    minimum: Math.round(minimum * 100) / 100,
    maximum: Math.round(maximum * 100) / 100,
  }
}

const RANGE_CONFIG = {
  '24h': { label: 'Last 24 hours', points: 48, stepMs: 30 * 60 * 1000 },
  '7d': { label: 'Last 7 days', points: 56, stepMs: 3 * 60 * 60 * 1000 },
  '30d': { label: 'Last 30 days', points: 60, stepMs: 12 * 60 * 60 * 1000 },
  '365d': { label: 'Last 365 days', points: 52, stepMs: 7 * 24 * 60 * 60 * 1000 },
}

/**
 * @param {Date} [now]
 * @param {'24h'|'7d'|'30d'|'365d'} [rangeKey]
 */
export function getSystemStatusSnapshot(now = new Date(), rangeKey = '30d') {
  const dateKey = utcDateKey(now)
  const rng = createRng(hashString(`agva-status:${dateKey}`))

  const checkIntervalMs = CHECK_INTERVAL_MINUTES * 60 * 1000
  const lastCheckAt = Math.floor(now.getTime() / checkIntervalMs) * checkIntervalMs
  const secondsSinceCheck = Math.floor((now.getTime() - lastCheckAt) / 1000)

  // Stable "uptime since" anchored to a fake start ~1.5 years before today (UTC day).
  const uptimeStartOffsetDays = 550 + Math.floor(rng() * 20)
  const uptimeMinutes = uptimeStartOffsetDays * 24 * 60
    + now.getUTCHours() * 60
    + now.getUTCMinutes()

  const range = RANGE_CONFIG[rangeKey] || RANGE_CONFIG['30d']
  const seriesRng = createRng(hashString(`agva-status-series:${dateKey}:${rangeKey}`))
  const { values, categories } = buildSeries(
    seriesRng,
    range.points,
    lastCheckAt,
    range.stepMs,
  )
  const summary = summarize(values)

  const periodCards = [
    {
      key: '7d',
      label: 'Last 7 days',
      uptime: '100%',
      detail: '0 incidents, 0m down',
    },
    {
      key: '30d',
      label: 'Last 30 days',
      uptime: '100%',
      detail: '0 incidents, 0m down',
    },
    {
      key: '365d',
      label: 'Last 365 days',
      uptime: '100%',
      detail: '0 incidents, 0m down',
    },
  ]

  return {
    dateKey,
    status: 'Up',
    statusTone: 'ok',
    uptimeLabel: `Currently up for ${formatDuration(uptimeMinutes)}`,
    lastCheckLabel: formatAgo(secondsSinceCheck),
    checkIntervalLabel: `Checked every ${CHECK_INTERVAL_MINUTES} minutes`,
    last24Hours: {
      uptime: '100%',
      detail: '0 incidents, 0m down',
    },
    rangeKey,
    rangeOptions: Object.entries(RANGE_CONFIG).map(([key, config]) => ({
      key,
      label: config.label,
    })),
    chart: {
      categories,
      values,
      ...summary,
    },
    periodCards,
    blurb:
      'The response-time chart above shows AGVA availability over time. Values are currently simulated from a shared UTC-day seed and will be replaced with live monitoring later.',
  }
}

export { RANGE_CONFIG }
